import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
    Container,
    KpiCard,
    Section as UiSection,
    SegmentedControl,
    ViewAs,
} from "@maeiz/ui";
import { useState } from "react";
import type { ViewAsMode } from "@maeiz/ui";
import { DocPage, Section, Demo, SpecTable, DoDont } from "../../components/DocChrome";

export function CardsPage() {
    const [view, setView] = useState<ViewAsMode>("grid");
    const [period, setPeriod] = useState("month");

    return (
        <DocPage
            title="Cards & Containers"
            description="Composed Card slots. Container surfaces. SegmentedControl for any pill set. No hover lift."
        >
            <Section title="Card composition">
                <Demo className="grid gap-4 sm:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>Default card</CardTitle>
                            <CardDescription>Header + body + footer slots</CardDescription>
                        </CardHeader>
                        <CardContent>
                            Quiet surface for lists, forms, and detail panels.
                        </CardContent>
                        <CardFooter>
                            <Button size="sm">Open</Button>
                            <Button size="sm" variant="ghost">
                                Dismiss
                            </Button>
                        </CardFooter>
                    </Card>
                    <Card variant="elevated">
                        <CardHeader>
                            <CardTitle>Elevated</CardTitle>
                            <CardDescription>Stronger shadow — still no motion</CardDescription>
                        </CardHeader>
                        <CardContent>Use for primary focus panels on dense dashboards.</CardContent>
                    </Card>
                    <Card variant="outlined" padding="lg">
                        <CardTitle>Outlined</CardTitle>
                        <CardDescription className="mt-1">
                            Border-led, no fill emphasis
                        </CardDescription>
                    </Card>
                    <Card variant="ghost" padding="sm">
                        <CardTitle>Ghost</CardTitle>
                        <CardDescription className="mt-1">
                            Transparent — nests inside surfaces
                        </CardDescription>
                    </Card>
                </Demo>
            </Section>

            <Section title="KPI + segmented">
                <Demo className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <ViewAs value={view} onChange={setView} />
                        <SegmentedControl
                            label="Filtered by"
                            value={period}
                            onChange={setPeriod}
                            options={[
                                { value: "today", label: "Today" },
                                { value: "week", label: "This week" },
                                { value: "month", label: "This month" },
                                { value: "year", label: "This year" },
                            ]}
                        />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <KpiCard
                            label="Cash owed"
                            value="₨ 12,450"
                            accent="gold"
                            hint="Positive = customer owes"
                        />
                        <KpiCard label="Open shifts" value="3" accent="primary" />
                    </div>
                </Demo>
            </Section>

            <Section title="Section + Container">
                <Demo>
                    <Container surface>
                        <UiSection
                            title="Deliveries today"
                            subtitle="Grouped region with optional actions"
                            actions={
                                <Button size="sm" variant="outline">
                                    Export
                                </Button>
                            }
                            padding="none"
                        >
                            <p className="text-sm text-neutral-secondary">
                                Section body sits inside a surfaced Container.
                            </p>
                        </UiSection>
                    </Container>
                </Demo>
            </Section>

            <Section title="Variants">
                <SpecTable
                    rows={[
                        ["default", "Soft shadow + border"],
                        ["elevated", "Stronger elevation"],
                        ["outlined", "Border only"],
                        ["ghost", "No chrome — nesting"],
                        ["SegmentedControl", "Any label + options; h-11 shell, shorter pills"],
                    ]}
                />
            </Section>

            <DoDont
                doItems={[
                    "Compose with Header / Content / Footer",
                    "Pass your own options to SegmentedControl",
                ]}
                dontItems={[
                    "hover:-translate-y",
                    "Nested card spam",
                    "Hardcode ViewAs for non-layout filters",
                ]}
            />
        </DocPage>
    );
}
